import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useLegalScrollSpy } from "../hooks/useLegalScrollSpy";
import { buildTocEntries } from "../lib/legal/toc";
import type { LegalSection } from "../lib/legal/types";

type LegalTableOfContentsProps = {
  sections: LegalSection[];
  /** Shorter panel for the mobile fixed bottom bar. */
  variant?: "default" | "compact";
};

/** Scroll only inside the TOC list — never the document. */
function scrollTocLinkIntoView(
  container: HTMLElement,
  link: HTMLElement,
): void {
  const containerRect = container.getBoundingClientRect();
  const linkRect = link.getBoundingClientRect();

  if (
    linkRect.top >= containerRect.top &&
    linkRect.bottom <= containerRect.bottom
  ) {
    return;
  }

  const delta =
    linkRect.top -
    containerRect.top -
    container.clientHeight / 2 +
    linkRect.height / 2;
  container.scrollTop = Math.max(0, container.scrollTop + delta);
}

export function LegalTableOfContents({
  sections,
  variant = "default",
}: LegalTableOfContentsProps) {
  const { t } = useTranslation();
  const entries = buildTocEntries(sections);
  const sectionIds = entries.map((e) => e.id);
  const activeId = useLegalScrollSpy(sectionIds);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevActiveRef = useRef<string | null>(null);
  const isCompact = variant === "compact";
  const activeEntry = entries.find((e) => e.id === activeId);

  useEffect(() => {
    if (!activeId || !scrollRef.current) return;
    if (prevActiveRef.current === activeId) return;
    prevActiveRef.current = activeId;

    const link = scrollRef.current.querySelector<HTMLAnchorElement>(
      `a[href="#${CSS.escape(activeId)}"]`,
    );
    if (!link) return;

    scrollTocLinkIntoView(scrollRef.current, link);
  }, [activeId]);

  const maxHeight = isCompact
    ? "max-h-[11rem]"
    : "max-h-[calc(100vh-7rem)]";

  return (
    <nav
      aria-label={t("legal.on_this_page")}
      className={
        isCompact
          ? "flex flex-col"
          : `flex flex-col overflow-hidden rounded-xl border border-border-soft bg-bg-subtle/95 shadow-sm backdrop-blur-sm dark:bg-bg-muted/95 ${maxHeight}`
      }
      lang="en"
    >
      <div
        className={
          isCompact
            ? "mb-2"
            : "shrink-0 border-b border-border-soft px-4 py-3"
        }
      >
        <h2
          className={
            isCompact
              ? "text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-text-subtle"
              : "text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle"
          }
        >
          {t("legal.table_of_contents")}
        </h2>
        {activeEntry ? (
          <p
            className={
              isCompact
                ? "mt-1 line-clamp-1 text-xs font-medium text-brand-500"
                : "mt-2 line-clamp-2 text-xs leading-snug text-brand-500"
            }
          >
            {t("legal.reading_now", { section: activeEntry.heading })}
          </p>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className={`overflow-y-auto overscroll-contain ${isCompact ? "max-h-[7rem] -mx-1 px-1" : "min-h-0 flex-1 px-2 py-2"}`}
      >
        <table className="w-full border-collapse text-left text-sm">
          {!isCompact ? (
            <thead>
              <tr className="border-b border-border-soft">
                <th
                  scope="col"
                  className="w-10 px-2 py-2 text-xs font-semibold uppercase text-text-subtle"
                >
                  {t("legal.section_col")}
                </th>
                <th
                  scope="col"
                  className="px-2 py-2 text-xs font-semibold uppercase text-text-subtle"
                >
                  {t("legal.topic_col")}
                </th>
              </tr>
            </thead>
          ) : null}
          <tbody>
            {entries.map((entry) => {
              const isActive = activeId === entry.id;
              return (
                <tr
                  key={entry.id}
                  className={`border-b border-border-soft/80 last:border-b-0 transition-colors duration-150 ${
                    isActive ? "bg-brand-500/10" : ""
                  }`}
                >
                  {!isCompact ? (
                    <td
                      className={`px-2 py-2 align-top font-medium tabular-nums ${
                        isActive ? "text-brand-500" : "text-text-subtle"
                      }`}
                    >
                      {entry.sectionLabel}
                    </td>
                  ) : null}
                  <td
                    colSpan={isCompact ? 2 : 1}
                    className={`py-1.5 align-top ${entry.depth === 2 ? "ps-4" : isCompact ? "" : "px-2"}`}
                  >
                    <a
                      href={`#${entry.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(entry.id);
                        if (!el) return;
                        el.scrollIntoView({ behavior: "smooth" });
                        window.history.replaceState(null, "", `#${entry.id}`);
                      }}
                      className={`block rounded-md leading-snug transition-colors duration-150 hover:text-brand-500 ${
                        isCompact ? "text-xs" : "text-sm"
                      } ${
                        entry.depth === 2 && !isCompact
                          ? "text-xs sm:text-sm"
                          : ""
                      } ${
                        isActive
                          ? "font-semibold text-brand-500"
                          : "text-text-muted"
                      }`}
                      aria-current={isActive ? "location" : undefined}
                    >
                      {isCompact && entry.sectionLabel !== "—"
                        ? `${entry.sectionLabel}. `
                        : null}
                      {entry.heading}
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </nav>
  );
}
